import type { Source } from "@shared/schema";
import { safeIsoString } from "@/lib/safeDate";
import type { KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import {
  getSpaceIdsFromKbItem,
  getTopicIdsFromKbItem,
} from "@/lib/kbDocumentLinks";

export interface UnifiedLibraryItem {
  id: string;
  title: string;
  type: string;
  itemType: "note" | "reference";
  content?: string | null;
  url?: string | null;
  folderId?: string | null;
  linkedSpaceIds: string[];
  linkedTopicIds: string[];
  linkedThreadIds: string[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
  originalItem: KnowledgeBaseItem | Source;
  isLocked?: boolean;
  includeInRag?: boolean;
}

function parseKbMetadata(item: KnowledgeBaseItem): Record<string, unknown> {
  if (!item.metadata) return {};
  if (typeof item.metadata === "object") return item.metadata as Record<string, unknown>;
  if (typeof item.metadata === "string") {
    try {
      return JSON.parse(item.metadata) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

export function kbItemToUnifiedLibraryItem(
  item: KnowledgeBaseItem
): UnifiedLibraryItem {
  const meta = parseKbMetadata(item);
  const fromMeta =
    meta.isLocked === true || meta.isLocked === "true" || !!meta.lockedAt;
  const isLocked = !!item.isLocked || !!item.lockedAt || fromMeta;

  return {
    id: item.id,
    title: item.title || "Untitled Note",
    type: item.type || "note",
    itemType: "note",
    content: item.content,
    url: null,
    folderId: item.folderId || null,
    linkedSpaceIds: getSpaceIdsFromKbItem(item),
    linkedTopicIds: getTopicIdsFromKbItem(item),
    linkedThreadIds: [],
    tags: item.tagIds || [],
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
    originalItem: item,
    isLocked: !!isLocked,
    includeInRag: item.includeInRag !== false,
  };
}

export function sourceToUnifiedLibraryItem(source: Source): UnifiedLibraryItem {
  const s = source as Source & {
    linkedTopicIds?: string[];
    topics?: string[];
    linkedThreads?: string[];
    tags?: string[];
    folderId?: string | null;
    linkedSpaceIds?: string[];
  };

  return {
    id: source.id,
    title: source.title || "Untitled Reference",
    type: source.type || "reference",
    itemType: "reference",
    content: source.content,
    url: source.url,
    folderId: s.folderId || null,
    linkedSpaceIds: s.linkedSpaceIds || [],
    linkedTopicIds: s.linkedTopicIds || s.topics || [],
    linkedThreadIds: s.linkedThreads || [],
    tags: s.tags || [],
    createdAt:
      typeof source.createdAt === "string"
        ? source.createdAt
        : safeIsoString(source.createdAt, new Date().toISOString()),
    updatedAt:
      typeof source.updatedAt === "string"
        ? source.updatedAt
        : safeIsoString(source.updatedAt, new Date().toISOString()),
    originalItem: source,
  };
}
